import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Search, X, FileText } from 'lucide-react';
import { Input } from '../ui/input';

// Define searchable content with actual text content from translations
const getSearchableContent = (t, language) => {
  return [
    { 
      id: 'home', 
      path: '/',
      title: t('nav.home'),
      sections: [
        { label: t('companyIntro.title'), content: `${t('companyIntro.paragraph1')} ${t('companyIntro.paragraph2')}` },
        { label: t('whyProsafe.title'), content: t('whyProsafe.reasons')?.join(' ') || '' },
        { label: t('missionVision.missionTitle'), content: t('missionVision.missionText') },
        { label: t('missionVision.visionTitle'), content: t('missionVision.visionText') },
        { label: t('coreValues.title'), content: t('coreValues.values')?.map(v => `${v.title} ${v.description}`).join(' ') || '' }
      ]
    },
    { 
      id: 'brandblussystemen', 
      path: '/diensten/brandblussystemen',
      title: t('nav.brandblussystemen'),
      sections: [
        { label: t('brandblussystemen.title'), content: `${t('brandblussystemen.subtitle')} ${t('brandblussystemen.paragraph1')} ${t('brandblussystemen.paragraph2')}` }
      ]
    },
    { 
      id: 'branddetectie', 
      path: '/diensten/branddetectie',
      title: t('nav.branddetectie'),
      sections: [
        { label: t('branddetectie.title'), content: `${t('branddetectie.subtitle')} ${t('branddetectie.paragraph1')} ${t('branddetectie.paragraph2')}` }
      ]
    },
    { 
      id: 'onderhoud', 
      path: '/diensten/onderhoud',
      title: t('nav.onderhoud'),
      sections: [
        { label: t('onderhoud.title'), content: `${t('onderhoud.subtitle')} ${t('onderhoud.paragraph1')} ${t('onderhoud.paragraph2')}` }
      ]
    },
    { 
      id: 'advies', 
      path: '/diensten/advies',
      title: t('nav.advies'),
      sections: [
        { label: t('advies.title'), content: `${t('advies.subtitle')} ${t('advies.paragraph1')} ${t('advies.paragraph2')}` }
      ]
    },
    { 
      id: 'noodverlichting', 
      path: '/diensten/noodverlichting',
      title: t('nav.noodverlichting'),
      sections: [
        { label: t('noodverlichting.title'), content: `${t('noodverlichting.subtitle')} ${t('noodverlichting.paragraph1')} ${t('noodverlichting.paragraph2')}` }
      ]
    },
    { 
      id: 'contact', 
      path: '/contact',
      title: t('nav.contact'),
      sections: [
        { label: t('contact.title'), content: `${t('contact.subtitle')} ${t('contact.address')} ${t('contact.phone')} ${t('contact.email')}` }
      ]
    }
  ];
};

const SearchModal = ({ isOpen, onClose }) => {
  const { t, language } = useLanguage();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    if (!isOpen) {
      setQuery('');
      setResults([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (query.trim() === '' || query.length < 2) {
      setResults([]);
      return;
    }

    const searchQuery = query.toLowerCase().trim();
    const searchableContent = getSearchableContent(t, language);
    const matchedResults = [];

    searchableContent.forEach(page => {
      // Check page title
      if (page.title?.toLowerCase().includes(searchQuery)) {
        matchedResults.push({
          pageId: page.id,
          path: page.path,
          pageTitle: page.title,
          matchedText: page.title,
          matchType: 'title'
        });
      }

      // Check each section's content
      page.sections.forEach(section => {
        const content = section.content?.toLowerCase() || '';
        if (content.includes(searchQuery)) {
          // Find the context around the match
          const index = content.indexOf(searchQuery);
          const start = Math.max(0, index - 40);
          const end = Math.min(content.length, index + searchQuery.length + 40);
          let snippet = section.content.substring(start, end);
          
          if (start > 0) snippet = '...' + snippet;
          if (end < content.length) snippet = snippet + '...';

          matchedResults.push({
            pageId: page.id,
            path: page.path,
            pageTitle: page.title,
            sectionLabel: section.label,
            matchedText: snippet,
            matchType: 'content'
          });
        }
      });
    });

    // Remove duplicates (keep first match per page)
    const uniqueResults = [];
    const seenPages = new Set();
    matchedResults.forEach(result => {
      if (!seenPages.has(result.pageId)) {
        seenPages.add(result.pageId);
        uniqueResults.push(result);
      }
    });

    setResults(uniqueResults.slice(0, 10)); // Limit to 10 results
  }, [query, t, language]);

  const handleResultClick = (result) => {
    // Store search term in sessionStorage for highlighting
    sessionStorage.setItem('searchHighlight', query.trim());
    
    // Close modal first
    onClose();
    setQuery('');
    
    // Small delay before navigation to ensure sessionStorage is set
    setTimeout(() => {
      window.location.href = result.path;
    }, 100);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
    if (e.key === 'Enter' && results.length > 0) {
      handleResultClick(results[0]);
    }
  };

  // Highlight the search term in the snippet
  const highlightMatch = (text, searchTerm) => {
    if (!text || !searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, i) => 
      regex.test(part) ? (
        <mark key={i} className="bg-[#e21b1b] text-white px-0.5 rounded">{part}</mark>
      ) : (
        part
      )
    );
  };

  if (!isOpen) return null;

  return (
    <div 
      data-testid="search-modal"
      className="fixed inset-0 bg-black/70 z-[9999] flex items-start justify-center pt-20"
      onClick={onClose}
    >
      <div 
        className="bg-[#1a1a1a] w-full max-w-2xl mx-4 rounded-lg shadow-2xl border border-gray-800 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center p-4 border-b border-gray-800">
          <Search className="w-5 h-5 text-gray-400 mr-3" />
          <Input
            ref={inputRef}
            data-testid="search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('search.placeholder')}
            className="flex-1 bg-transparent border-none text-white placeholder-gray-500 focus:ring-0 focus:outline-none text-base"
          />
          <button 
            data-testid="search-close-btn"
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {query.trim().length >= 2 && (
          <div className="max-h-96 overflow-y-auto">
            {results.length > 0 ? (
              <ul className="py-2">
                {results.map((result, index) => (
                  <li key={`${result.pageId}-${index}`}>
                    <button
                      data-testid={`search-result-${result.pageId}`}
                      onClick={() => handleResultClick(result)}
                      className="w-full text-left px-4 py-3 hover:bg-[#e21b1b]/10 transition-colors group"
                    >
                      <div className="flex items-start gap-3">
                        <FileText className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-roboto font-medium text-white group-hover:text-[#e21b1b] transition-colors">
                            {result.pageTitle}
                          </div>
                          {result.sectionLabel && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {result.sectionLabel}
                            </div>
                          )}
                          <div className="text-sm text-gray-400 mt-1 line-clamp-2">
                            {highlightMatch(result.matchedText, query)}
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-4 py-6 text-gray-500 text-center font-roboto">
                {t('search.noResults')}
              </p>
            )}
          </div>
        )}

        {query.trim().length > 0 && query.trim().length < 2 && (
          <p className="px-4 py-4 text-gray-500 text-center font-roboto text-sm">
            {language === 'nl' ? 'Typ minimaal 2 karakters...' : 'Type at least 2 characters...'}
          </p>
        )}
      </div>
    </div>
  );
};

export default SearchModal;
